import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";

import { useGuestPortalContext } from "./GuestPortalContext";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Spin from "components/spin/spin";
import "./Navigation/portalNavigation.scss";
import "./portalPaymentMethods.scss";
import { capitalize, getCreditCardIcon } from "helpers/Helpers";
import { ButtonNew as Button } from "components/buttonNew";
import Popup from "components/popup/Popup";
import Callout from "components/callout/Callout";
import Sheet from "components/sheet/Sheet";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import CardSection from "components/cardSection/CardSection";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DeleteCustomerPaymentMethod,
  GetCustomerPaymentMethods,
  ICustomerPaymentMethod,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/customer/paymentMethod";
import axios, { CancelToken } from "axios";
import Panel from "components/panel/Panel";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import BottomSheet from "components/bottomSheet/BottomSheet";
import Tabs from "components/tabs/Tabs";
import { GetCustomerGiftCards, GetGiftCardPDF, IGiftCard } from "api/rpc/2024-04/customer/payment/giftCard";
import { QRCode } from "react-qrcode-logo";
import DataTable from "components/dataTable/DataTable";
import { LocaleCurrency } from "helpers/Locale";
import { Badge } from "components/badge/Badge";
import { faDownload } from "@fortawesome/pro-regular-svg-icons";

const TABLE_LIMIT = 50;
interface IState {
  giftCards: IGiftCard[];
  showGiftCard: boolean;
  selectedGiftCard: IGiftCard;
  paymentMethods: Array<ICustomerPaymentMethod>;
  deletePopupVisible: boolean;
  selectedPaymentMethodId: number;
  newCardVisible: boolean;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  offset: number;
}

export default function PortalPaymentMethods() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { portalState } = useGuestPortalContext();
  const guest = portalState.guest;
  const [state, setState] = useState<IState>({
    paymentMethods: null,
    showGiftCard: false,
    selectedGiftCard: null,
    giftCards: null,
    deletePopupVisible: false,
    newCardVisible: false,
    selectedPaymentMethodId: null,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    offset: 0,
  });

  const windowSize = useWindowSize();
  const stripe = useStripe();
  const elements = useElements();
  const [selectedTab, setSelectedTab] = useState<number>(0);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadPaymentMethods(source.token);

    return () => {
      source.cancel("Cancelled");
    };
  }, []);

  useEffect(() => {
    if (selectedTab === 1) {
      setState(prevState => ({ ...prevState, giftCards: undefined }));
      const source = axios.CancelToken.source();
      void loadGiftCards(source.token);
    }
  }, [selectedTab]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadGiftCards(source.token);
    return () => {
      source.cancel();
    };
  }, [state.offset]);

  const paymentMethodsTabs = [
    {
      id: "creditCards",
      content: "Credit Cards",
    },
    {
      id: "giftCards",
      content: "Gift Cards",
    },
  ];

  async function loadPaymentMethods(cancelToken?: CancelToken) {
    const paymentMethodRes = await GetCustomerPaymentMethods({ type: ["card"], include_expired: true }, false, cancelToken);
    if (paymentMethodRes?.status !== StatusCode.OK) {
      if (paymentMethodRes.message === "Cancelled") {
        return;
      }
      dispatch(showError(t("Error getting payment methods")));
      setState(prevState => ({ ...prevState, paymentMethods: [] }));
      return;
    }
    setState(prevState => ({ ...prevState, paymentMethods: paymentMethodRes?.data?.card }));
  }

  async function loadGiftCards(cancelToken?: CancelToken) {
    setState(prevState => ({ ...prevState, giftCards: null }));
    const giftCardRes = await GetCustomerGiftCards({ customer_id: guest.id, offset: state.offset }, false, cancelToken);
    if (giftCardRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting gift cards")));
      setState(prevState => ({ ...prevState, giftCards: [] }));
      return;
    }

    const giftCards = giftCardRes?.data;

    const sortedGiftCards = [...(giftCards ?? [])].sort((a, b) => {
      const getRank = (card: any) => {
        if (card.status !== "valid") {
          return 2;
        }
        if (card.balance === 0) {
          return 1;
        }
        return 0;
      };

      const rankA = getRank(a);
      const rankB = getRank(b);

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      return 0;
    });

    setState(prevState => ({ ...prevState, giftCards: sortedGiftCards }));
  }

  async function deletePaymentMethod() {
    if (state.selectedPaymentMethodId) {
      const deleteRes = await DeleteCustomerPaymentMethod({ id: state.selectedPaymentMethodId }, true);
      if (deleteRes?.status !== StatusCode.OK) {
        dispatch(showError(typeof deleteRes?.data === "string" ? deleteRes.data : "Error deleting payment method"));
        return;
      }
      dispatch(showSuccess(t("Successfully deleted payment method")));
      setState(prevState => ({
        ...prevState,
        deletePopupVisible: false,
        selectedPaymentMethodId: null,
        paymentMethods: null,
      }));
      void loadPaymentMethods();
    }
  }

  function openSelectedGiftCard(e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, selectedGiftCard: IGiftCard) {
    e.stopPropagation();

    setState(prev => ({ ...prev, showGiftCard: true, selectedGiftCard: selectedGiftCard }));
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setState(prevState => ({
      ...prevState,
      elementComplete: { ...state.elementComplete, [e.elementType]: e.complete },
    }));
  }

  function closeNewCardModal() {
    elements.getElement(CardNumberElement).clear();
    elements.getElement(CardExpiryElement).clear();
    elements.getElement(CardCvcElement).clear();
    setState(prevState => ({ ...prevState, newCardVisible: false }));
  }

  async function handleDownloadGiftCard(e: React.MouseEvent<HTMLElement, MouseEvent>, gift_card_id: number) {
    e.stopPropagation();
    const downloadGiftCardRes = await GetGiftCardPDF({ gift_card_id: gift_card_id, customer_id: guest.id }, true);

    if (downloadGiftCardRes.status !== StatusCode.OK) {
      dispatch(showError(t('Error retrieving gift card PDF')));
      return;
    }

    window.open(downloadGiftCardRes.data);
  }

  async function handleAddNewCard() {
    if (!stripe || !elements) {
      dispatch(showError(t("Error with payment method setup")));
      return;
    }
    dispatch(enqueue());
    const stripePaymentMethodRes = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement("cardNumber"),
    });

    if (stripePaymentMethodRes.paymentMethod) {
      const setupRes = await PostSetup(false);

      if (setupRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error setting up payment method")));
      }
      if (stripePaymentMethodRes?.paymentMethod?.id !== "undefined" && setupRes.status === StatusCode.OK) {
        const stripeConfirmCardRes = await stripe.confirmCardSetup(String(setupRes.data.setup_intent.client_secret), {
          payment_method: stripePaymentMethodRes.paymentMethod.id,
        });
        if (stripeConfirmCardRes.error) {
          dispatch(showError(stripeConfirmCardRes.error.message));
        } else {
          const postPaymentRes = await PostPaymentMethod(
            {
              payment_method_id: stripeConfirmCardRes.setupIntent.payment_method,
              save_card: true,
            },
            false,
          );
          if (postPaymentRes.status !== StatusCode.OK) {
            dispatch(showError(t("Error creating payment method")));
          } else {
            dispatch(showSuccess(t("Successfully added new card")));
            setState(prevState => ({
              ...prevState,
              paymentMethods: null,
            }));
            void closeNewCardModal();
            void loadPaymentMethods();
          }
        }
      }
    } else {
      dispatch(showError(stripePaymentMethodRes.error.message));
    }

    dispatch(dequeue());
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setState(prev => ({ ...prev, offset: state.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setState(prev => ({ ...prev, offset: state.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  const primaryAction = {
    content: "Add Credit Card",
    action: () => setState(prevState => ({ ...prevState, newCardVisible: true })),
  };


  // Check if card inputs are filled
  const elementsComplete =
    state.elementComplete.cardNumber === true &&
    state.elementComplete.cardExpiry === true &&
    state.elementComplete.cardCvc === true;

  console.log('state', state);
  return (
    <>
      <Page title={t("Payment Methods")} full secondaryActions={selectedTab === 0 ? [primaryAction] : null}>
        <Tabs tabs={paymentMethodsTabs} selected={selectedTab} onSelect={setSelectedTab}>
          {selectedTab === 0 ? (
            state?.paymentMethods ? (
              state?.paymentMethods?.length > 0 ? (
                <div className="mt-4">
                  {state.paymentMethods?.map((paymentMethod, index) => {
                    const iconName = getCreditCardIcon(paymentMethod?.brand);
                    return (
                      <div key={index} className="portal-payment-method-container">
                        <div className="portal-payment-method-credit-card-container">
                          <div className="portal-payment-method-credit-card-icon">
                            <FontAwesomeIcon icon={iconName} />
                          </div>

                          <div className="flex-column">
                            <p className="payment-subtext">
                              <span className="card-number">•••• •••• ••••</span> {paymentMethod?.last4}
                            </p>
                            <p className="payment-expiry-text">
                              {t("Expires On")}: {String(paymentMethod?.exp_month).padStart(2, "0")}/{paymentMethod?.exp_year}
                            </p>
                          </div>
                        </div>

                        <div className="portal-payment-method-actions">
                          {paymentMethod?.status === "expired" && (
                            <Badge type="error" size="small">
                              {t("Expired")}
                            </Badge>
                          )}
                          <Button
                            size="small"
                            type="text"
                            onClick={() =>
                              setState(prevState => ({
                                ...prevState,
                                deletePopupVisible: true,
                                selectedPaymentMethodId: paymentMethod?.id,
                              }))
                            }
                          >
                            <FontAwesomeIcon icon={["far", "trash-can"]} className="mr-1" />
                            {t("Delete")}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Callout
                  type="info"
                  title={t("No Saved Payment Methods")}
                  content={t("You do not have any saved payment methods")}
                />
              )
            ) : (
              <div className="portal-spinner">
                <span>
                  <Spin />
                </span>
              </div>
            )
          ) : null}

          {selectedTab === 1 ? (
            <div className="mt-4">
              <DataTable
                columns={[
                  { label: t("Gift Card No.") },
                  { label: t("Balance") },
                  { label: t("Currency") },
                  { label: t("Status") },
                  { label: "", width: '5%' },
                ]}
                loading={!state.giftCards}
                footer={{
                  tableLimit: TABLE_LIMIT,
                  tableOffset: state.offset,
                  handleTableOffset: direction => handleTableNavigation(direction),
                  disableNextOffset: !(state.giftCards?.length === TABLE_LIMIT) || state.giftCards === undefined,
                }}
              >

                {state.giftCards?.map(giftCard => {
                  return (
                    <tr key={giftCard.id} onClick={e => openSelectedGiftCard(e, giftCard)}>
                      <td>{`**** **** **** ${giftCard.last4}`}</td>
                      <td>
                        <LocaleCurrency currency={giftCard.currency} amount={giftCard.balance} />
                      </td>
                      <td>{giftCard.currency.toUpperCase()}</td>
                      <td>
                        {giftCard.status.includes("disabled") ? (
                          <Badge type="warning">{t('Invalid')}</Badge>
                        ) : (
                          <Badge type="success">{capitalize(giftCard.status)}</Badge>
                        )}
                      </td>
                      <td>
                        <Button onClick={e => handleDownloadGiftCard(e, giftCard.id)} type="link">
                          <FontAwesomeIcon icon={faDownload} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            </div>
          ) : null}
        </Tabs>
      </Page>

      {/* QR Code Viwer */}

      {/*  */}
      {/* Add new credit card modal */}
      <Panel
        open={state.newCardVisible && windowSize.width < MOBILE_WIDTH}
        onClose={closeNewCardModal}
        title={t("Add Card")}
        animate
        primaryAction={{
          onClick: handleAddNewCard,
          label: t("Add Card"),
          disabled: !elementsComplete ? true : false,
        }}
        secondaryAction={{
          onClick: closeNewCardModal,
          label: t("Cancel"),
        }}
      >
        <div>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                {windowSize.width < MOBILE_WIDTH && <CardSection onChange={handleCardSectionChange} type="one-row" />}
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </div>
      </Panel>

      <Sheet
        size="small"
        height="flexible"
        open={state.newCardVisible && windowSize.width > MOBILE_WIDTH}
        closable
        title={t("Add New Credit Card")}
        cancelText={t("Cancel")}
        okText={t("Add Card")}
        onOk={handleAddNewCard}
        okDisabled={!elementsComplete ? true : false}
        onCancel={closeNewCardModal}
        backDropCancel={false}
      >
        <div>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                {windowSize.width > MOBILE_WIDTH && <CardSection onChange={handleCardSectionChange} type="one-row" />}
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </div>
      </Sheet>

      <Sheet
        size="small"
        height="flexible"
        open={state.showGiftCard && windowSize.width > MOBILE_WIDTH}
        closable
        title={t("Gift Card")}
        okText={t("Close")}
        hideCancelButton
        onOk={() => setState(prevState => ({ ...prevState, showGiftCard: false, selectedGiftCard: null }))}
        onCancel={() => setState(prevState => ({ ...prevState, showGiftCard: false, selectedGiftCard: null }))}
        backDropCancel={false}
      >
        <div>
          <div>
            <div className="flex justify-center text-2xl font-bold">{state?.selectedGiftCard?.code}</div>
            <div className="flex justify-center mb-2">
              <QRCode value={state?.selectedGiftCard?.code} size={180} eyeRadius={5} qrStyle="fluid" ecLevel="H" />
            </div>
            <div className="flex justify-center">Pin: {state?.selectedGiftCard?.pin}</div>
          </div>
        </div>
      </Sheet>

      <Panel
        open={state.showGiftCard && windowSize.width <= MOBILE_WIDTH}
        onClose={() => setState(prevState => ({ ...prevState, showGiftCard: false, selectedGiftCard: null }))}
        title={t("Gift Card")}
        animate
        primaryAction={{
          onClick: () => setState(prevState => ({ ...prevState, showGiftCard: false, selectedGiftCard: null })),
          label: t("Close"),
        }}
      >
        <div>
          <div>
            <div className="flex justify-center mt-5 mb-2">
              <QRCode value={state?.selectedGiftCard?.code} size={180} eyeRadius={5} qrStyle="fluid" ecLevel="H" />
            </div>
            <div className="flex justify-center">Pin: {state?.selectedGiftCard?.pin}</div>
          </div>
        </div>
      </Panel>

      <Popup
        open={state.deletePopupVisible && windowSize.width > MOBILE_WIDTH}
        onCancel={() =>
          setState(prevState => ({ ...prevState, deletePopupVisible: false, selectedPaymentMethodId: null }))
        }
        onOk={deletePaymentMethod}
        type="warning"
        title={t("Delete Payment Method?")}
        description={t("Are you sure you want to delete the selected payment method?")}
      />

      <BottomSheet
        open={state.deletePopupVisible && windowSize.width <= MOBILE_WIDTH}
        onClose={() =>
          setState(prevState => ({ ...prevState, deletePopupVisible: false, selectedPaymentMethodId: null }))
        }
        type="warning"
        content={t("Are you sure you want to delete the selected payment method?")}
        primaryAction={{ label: t("Delete"), onClick: deletePaymentMethod }}
      />
    </>
  );
}